import { Alert, Button, DatePicker, Form, Input, Segmented, Select, Spin, Typography } from 'antd'
import { useMemo, useState } from 'react'
import dayjs from 'dayjs'
import VSpace from '../../../common/VSpace'
import SpendBreakdownTable from './SpendBreakdownTable'
import SpendSummaryCards from './SpendSummaryCards'
import SpendTrendChart from './SpendTrendChart'
import {
  SPEND_BUCKET_OPTIONS,
  SpendBucketSize,
  SpendFilters,
  SpendFormValues,
  SpendGrouping,
  getDefaultSpendFilters,
  getSpendWarnings,
  PlanOption,
  toChartSeries,
  toPlanOptions,
  toSpendFilters,
} from './spendModel'
import {
  useSpendBreakdown,
  useSpendPlanOptions,
  useSpendSummary,
  useSpendTimeseries,
} from './spendQueries'

const { RangePicker } = DatePicker

const BREAKDOWN_LIMIT = 20

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Could not load spend data.'

const SpendPanel = () => {
  const [form] = Form.useForm<SpendFormValues>()
  const [filters, setFilters] = useState<SpendFilters>(() => getDefaultSpendFilters())
  const [bucket, setBucket] = useState<SpendBucketSize>('day')
  const [grouping, setGrouping] = useState<SpendGrouping>('tenant')
  // Plan choices load on first open, never with the panel: a plan request
  // failing (e.g. chat not yet serving plans) must not touch anything else.
  const [planFilterOpened, setPlanFilterOpened] = useState(false)
  const selectedPlan = Form.useWatch('planName', form)
  // The label of a plan picked from a breakdown row, so the filter can show
  // it before (or without) the choices having loaded.
  const [pickedPlan, setPickedPlan] = useState<PlanOption>()

  const defaultDateRange = useMemo<[dayjs.Dayjs, dayjs.Dayjs]>(() => {
    const defaults = getDefaultSpendFilters()
    return [dayjs(defaults.from), dayjs(defaults.to)]
  }, [])

  const summaryQuery = useSpendSummary(filters)
  const timeseriesQuery = useSpendTimeseries(filters, bucket)
  const breakdownQuery = useSpendBreakdown(filters, grouping, BREAKDOWN_LIMIT)
  const planOptionsQuery = useSpendPlanOptions(filters, BREAKDOWN_LIMIT, planFilterOpened)
  const planOptions = toPlanOptions(
    planOptionsQuery.data?.rows ?? [],
    pickedPlan?.value === selectedPlan ? pickedPlan : undefined,
  )

  const handleApply = () => {
    setFilters(toSpendFilters(form.getFieldsValue(), defaultDateRange))
  }

  // Adds the plan to the filters already applied. Reading the whole form here
  // would also apply anything typed into the other fields but not yet applied.
  const handleSelectPlan = (plan: PlanOption) => {
    setPickedPlan(plan)
    form.setFieldsValue({ planName: plan.value })
    setFilters((applied) => ({ ...applied, planName: plan.value }))
  }

  // With no choices this fills the empty-list slot; with choices it sits above
  // them, since once a plan is selected the list is never empty and the
  // empty-list slot would hide it.
  const planOptionsStatus = planOptionsQuery.isError ? (
    <Typography.Text type="danger" style={{ display: 'block', padding: '4px 12px' }}>
      {errorMessage(planOptionsQuery.error)}
    </Typography.Text>
  ) : planOptionsQuery.isFetching ? (
    <div style={{ padding: '4px 12px' }}>
      <Spin size="small" />
    </div>
  ) : null

  const summary = summaryQuery.data
  const warnings = summary ? getSpendWarnings(summary, filters.from) : []
  const points = useMemo(
    () => toChartSeries(timeseriesQuery.data?.buckets ?? [], bucket),
    [timeseriesQuery.data, bucket],
  )

  return (
    <VSpace size="middle">
      <Form
        form={form}
        onFinish={handleApply}
        initialValues={{ dateRange: defaultDateRange }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Form.Item name="dateRange" style={{ marginBottom: 0 }}>
            <RangePicker allowClear={false} />
          </Form.Item>
          <Form.Item name="tenantGuid" style={{ marginBottom: 0 }}>
            <Input placeholder="Tenant GUID" allowClear style={{ width: 300 }} />
          </Form.Item>
          <Form.Item name="model" style={{ marginBottom: 0 }}>
            <Input placeholder="Model" allowClear style={{ width: 220 }} />
          </Form.Item>
          <Form.Item name="client" style={{ marginBottom: 0 }}>
            <Input placeholder="App" allowClear style={{ width: 120 }} />
          </Form.Item>
          <div data-testid="spend-plan-filter">
            <Form.Item name="planName" style={{ marginBottom: 0 }}>
              <Select
                placeholder="All plans"
                allowClear
                style={{ width: 180 }}
                options={planOptions}
                notFoundContent={planOptionsStatus ?? 'No plan spend in this window'}
                popupRender={(menu) =>
                  planOptions.length > 0 ? (
                    <>
                      {planOptionsStatus}
                      {menu}
                    </>
                  ) : (
                    menu
                  )
                }
                onOpenChange={(open) => open && setPlanFilterOpened(true)}
              />
            </Form.Item>
          </div>
          <Button type="primary" htmlType="submit">
            Apply
          </Button>
          <Segmented
            value={bucket}
            options={SPEND_BUCKET_OPTIONS}
            onChange={(value) => setBucket(value as SpendBucketSize)}
          />
        </div>
      </Form>

      {/*
        A failed request must never render as a window with no spend. The
        window and allowlist rules live in the chat API and are not restated
        here, so its message is the only accurate explanation of a rejection.
      */}
      {summaryQuery.isError && (
        <Alert
          type="error"
          showIcon
          message="Could not load spend"
          description={errorMessage(summaryQuery.error)}
        />
      )}
      {timeseriesQuery.isError && (
        <Alert
          type="error"
          showIcon
          message="Could not load the trend"
          description={errorMessage(timeseriesQuery.error)}
        />
      )}

      {warnings.map((warning) => (
        <Alert key={warning.key} type="warning" showIcon message={warning.message} />
      ))}

      {summary && (
        <SpendSummaryCards
          totals={summary.totals}
          loading={summaryQuery.isFetching}
        />
      )}

      <SpendTrendChart
        points={points}
        bucket={bucket}
        loading={timeseriesQuery.isLoading}
      />

      {breakdownQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message="Could not load the breakdown"
          description={errorMessage(breakdownQuery.error)}
        />
      ) : (
        <SpendBreakdownTable
          rows={breakdownQuery.data?.rows ?? []}
          by={grouping}
          rowsBy={breakdownQuery.data?.by}
          onChangeGrouping={setGrouping}
          onSelectPlan={handleSelectPlan}
          loading={breakdownQuery.isFetching}
        />
      )}
    </VSpace>
  )
}

export default SpendPanel
